import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProgramsProvider } from './context/ProgramsContext';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import HostSpecialistDashboard from './pages/HostSpecialistDashboard';
import HostFinderPage from './pages/HostFinderPage';
import HostProspectsPage from './pages/HostProspectsPage';
import StudentsPage from './pages/StudentsPage';
import StudentDetailPage from './pages/StudentDetailPage';
import StudentPortalPage from './pages/StudentPortalPage';
import HostsPage from './pages/HostsPage';
import HostDetailPage from './pages/HostDetailPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import DocumentTrackerPage from './pages/DocumentTrackerPage';
import InboxPage from './pages/InboxPage';
import AvailabilityPage from './pages/AvailabilityPage';
import HostAvailabilityPage from './pages/HostAvailabilityPage';
import ForceChangePassword from './pages/ForceChangePassword';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Any authenticated user — students are redirected to their portal
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading…</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'student') return <Navigate to="/my-practicum" />;
  // First login on a temporary password → force a change before anything else.
  if (user.must_change_password) return <ForceChangePassword />;
  return children;
}

// Student portal — only authenticated users (any role), no staff redirect
function PortalRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading…</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" />;
  return children;
}

// Home dashboard — Host Specialists get their own view; everyone else the standard one.
function HomeDashboard() {
  const { user } = useAuth();
  return user?.is_host_specialist ? <HostSpecialistDashboard /> : <DashboardPage />;
}

function HostSpecialistRoute({ children }) {
  const { user } = useAuth();
  if (!user?.is_host_specialist) return <Navigate to="/" />;
  return children;
}

// Host Prospects — Host Specialist or admin
function ProspectRoute({ children }) {
  const { user } = useAuth();
  if (!user?.is_host_specialist && user?.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function ImpersonationBanner() {
  const { user, impersonating, stopImpersonating } = useAuth();
  if (!impersonating) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#b45309', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 16, padding: '8px 16px', fontSize: 13, fontWeight: 500,
    }}>
      <span>⚠️ Logged in as <strong>{user?.full_name}</strong> ({user?.role})</span>
      <button
        onClick={stopImpersonating}
        style={{
          background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
          color: '#fff', borderRadius: 6, padding: '3px 12px', cursor: 'pointer',
          fontSize: 12, fontWeight: 600,
        }}
      >
        ← Return to Admin
      </button>
    </div>
  );
}

function AppRoutes() {
  return (
    <Router>
      <ImpersonationBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public scheduling — no auth required */}
        <Route path="/availability/:token" element={<AvailabilityPage />} />
        <Route path="/host-availability/:token" element={<HostAvailabilityPage />} />

        {/* Student self-service portal */}
        <Route
          path="/my-practicum"
          element={<PortalRoute><StudentPortalPage /></PortalRoute>}
        />

        {/* Staff routes — students are bounced to /my-practicum by ProtectedRoute */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<HomeDashboard />} />
                  <Route path="/students" element={<StudentsPage />} />
                  <Route path="/students/:id" element={<StudentDetailPage />} />
                  <Route path="/hosts" element={<HostsPage />} />
                  <Route path="/hosts/:id" element={<HostDetailPage />} />
                  <Route path="/host-prospects" element={<ProspectRoute><HostProspectsPage /></ProspectRoute>} />
                  <Route path="/host-finder" element={<HostSpecialistRoute><HostFinderPage /></HostSpecialistRoute>} />
                  <Route path="/doc-tracker" element={<DocumentTrackerPage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
                  <Route path="/email-templates" element={<AdminRoute><EmailTemplatesPage /></AdminRoute>} />
                  <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ProgramsProvider>
        <AppRoutes />
      </ProgramsProvider>
    </AuthProvider>
  );
}
